/**
 * In-memory analytics identity for GTM/dataLayer.
 * Set from UserProvider when session is known; cleared on logout.
 * `user_status` is also seeded from auth cookies before GTM starts so native
 * GA4 events (page_view, session_start) can read it as a user property.
 */

import { getJwt, getSessionToken, getUserId } from "./authHeaders";

export type AnalyticsUserStatus = "logged_in" | "guest";

let analyticsUserId: string | null = null;
let analyticsUserStatus: AnalyticsUserStatus = "guest";

export function setAnalyticsUserId(userId: string | null | undefined): void {
  if (userId == null || userId === "") {
    analyticsUserId = null;
    return;
  }
  analyticsUserId = String(userId);
}

export function clearAnalyticsUserId(): void {
  analyticsUserId = null;
}

/** Non-null when a logged-in user is active (synced from UserContext). */
export function getAnalyticsUserId(): string | null {
  return analyticsUserId;
}

export function setAnalyticsUserStatus(status: AnalyticsUserStatus): void {
  analyticsUserStatus = status === "logged_in" ? "logged_in" : "guest";
}

export function getAnalyticsUserStatus(): AnalyticsUserStatus {
  return analyticsUserStatus;
}

/**
 * Best-effort status before session verify completes.
 * Auth cookies / stored user id imply the user was logged in on the previous page.
 */
export function inferAnalyticsUserStatusFromCookies(): AnalyticsUserStatus {
  if (typeof window === "undefined") return "guest";
  try {
    if (getJwt() || getSessionToken() || getUserId()) {
      return "logged_in";
    }
  } catch {
    /* ignore */
  }
  return "guest";
}

/**
 * Apply logged-in / guest identity in memory (does not push to dataLayer).
 * Pass a user id for logged_in; null/undefined clears to guest.
 */
export function applyAnalyticsIdentity(userId: string | null | undefined): void {
  if (userId != null && String(userId) !== "") {
    setAnalyticsUserId(userId);
    setAnalyticsUserStatus("logged_in");
  } else {
    clearAnalyticsUserId();
    setAnalyticsUserStatus("guest");
  }
}
